// The basemap registry — one definition of every raster tile layer the web
// clients draw, shared by the CDN Worker that proxies them and by the browsers
// that request them.
//
// @docs:
//   - docs/wip/CDN_ARCHITECTURE.md §3.3, §3.4, §7
//
// Before this existed the same layers were declared three times, with drifting
// zoom ceilings and three different Sentinel vintages: passagewiki
// `src/basemaps.ts`, vortex-track `VesselMap.tsx`, vortex-saas `MapView.tsx`.
// A provider is added here or nowhere.
//
// TWO NAMESPACES, per CDN_ARCHITECTURE §2.1. `/map/` is rendered cartography —
// someone drew it from vector data. `/imagery/` is photography — satellite or
// aerial. They are split because they behave differently: imagery is heavier,
// its licences are stricter and usually metered, and Phase 3 may put an
// entitlement check in front of `/imagery/` that `/map/` will never need.
//
// ⚠️ TERMS GATE. `termsStatus` is the switch. Proxying a provider means we
// re-serve their bytes from our host and keep a copy for days — two permissions
// most tile licences treat separately, and neither is implied by the tiles
// being reachable without a key. Until someone reads the actual terms, the CDN
// route refuses the provider and every client draws it direct, exactly as
// before.
//
// `prototype` is the deliberate exception, and it is not the same as
// `confirmed`: it means someone with authority chose to serve the provider on
// an unadvertised path while its terms are still being read. It exists so that
// choice is recorded rather than disguised as a licence review that happened.
// A prototype provider must not be linked from production UI, and must reach
// `confirmed` or `unread` before it ships.
//
// GOOGLE, BING AND AZURE. Tony, 30 Oct 2026: every satellite and chart source the
// Scout draws goes behind the tile front door so each tile is counted, Google and Bing
// on the undocumented endpoints the Scout uses today (`mt1.google.com/vt`, the retired
// `ecn.t{s}.tiles.virtualearth.net`) until he supplies licensed keys. Those endpoints
// carry no licence for proxying or caching, so they are `prototype`: front door only,
// never on the public CDN or the published manifest, and they must be licensed before
// the Scout change ships. Navionics stays direct.

/** Rendered cartography vs photography. Decides the CDN path prefix. */
export type MapNamespace = 'map' | 'imagery';

/**
 * Where a provider's licence stands. The CDN serves `confirmed` and
 * `prototype`; everything else 404s and clients draw direct.
 */
export type TermsStatus =
  /** Licence read: proxying AND caching for the stated TTL are permitted. */
  | 'confirmed'
  /** Served on an unadvertised path by an explicit decision, terms pending. */
  | 'prototype'
  /** Nobody has checked yet. The safe default. */
  | 'unread'
  /** The licence forbids the use — not a caching question. */
  | 'blocked';

/** The two states the CDN will actually serve. */
export const isServable = (status: TermsStatus): boolean =>
  status === 'confirmed' || status === 'prototype';

/** Public route order is always XYZ; `upstream` re-orders per provider. */
export interface MapProvider {
  id: string;
  label: string;
  namespace: MapNamespace;
  /** A base fills the map; an overlay draws on top of one. */
  kind: 'base' | 'overlay';
  /** Upstream template. `{z}` `{x}` `{y}` are substituted by name, so a ZYX
   *  provider simply writes them in its own order; `{quadkey}` is Bing's single
   *  address for all three. `{key}` is the credential. */
  upstream: string;
  /** Keyed upstream, used only when the Worker holds `credentialRef`. */
  keyedUpstream?: string;
  /** Which secret the Worker looks for; absent = the source is tokenless. */
  credentialRef?: string;
  /** No free address: without the secret the Worker has no tile, and the client draws direct. */
  credentialRequired?: boolean;
  extension: 'png' | 'jpg';
  contentType: 'image/png' | 'image/jpeg';
  /**
   * Deepest level a client may DISPLAY. Between maxNativeZoom and here the
   * client upscales the deepest real tile — it does not request one.
   */
  maxZoom: number;
  /**
   * Deepest level the provider actually holds, and therefore the deepest the
   * CDN route will fetch: above this there is nothing upstream to get, so the
   * route 404s rather than paying for a miss per tile.
   */
  maxNativeZoom: number;
  attribution: string;
  /** How long a fetched tile is kept, at the edge and in R2. */
  cacheTtlSeconds: number;
  termsStatus: TermsStatus;
  /** What was confirmed, or what still has to be — so the next reader knows. */
  termsNote: string;
  /**
   * Served on the public CDN (cdn.vortexnav.com /map and /imagery), which checks no pass:
   * anything there is open to anyone. Every other servable source is reached only through
   * the tile front door (vortex-tiles), which checks a pass and counts each tile.
   */
  publicCdn: boolean;
  /** False for a source customers are never charged for. Its tiles are still counted. */
  charged?: boolean;
  /** How deep the front door fetches, where the Scout asks deeper than the web ceiling. */
  frontDoorMaxZoom?: number;
  /**
   * Options a client may choose, each from a closed set. Each one keys the cache. An
   * option named in the template (`{c}`) is substituted; one that is not is appended as
   * `&name=value`, and left off at its default.
   */
  variants?: Readonly<Record<string, { values: readonly string[]; default: string }>>;
  /**
   * HTTP cache behaviour per route. R2 and edge retention always use `cacheTtlSeconds`;
   * this shapes Cache-Control on responses and edge Cache API entries.
   */
  cachePolicy?: ProviderCachePolicy;
}

/**
 * Per-provider HTTP caching. Workers call `cacheControlHeader()` — do not hand-roll
 * Cache-Control in vortex-cdn or vortex-tiles.
 */
export interface ProviderCachePolicy {
  /** Extra directives after max-age (e.g. `immutable` for versioned slippy-map tiles). */
  browserDirectives?: readonly string[];
  /** Override max-age on anonymous CDN responses; default `cacheTtlSeconds`. */
  cdnBrowserMaxAgeSeconds?: number;
  /** Override max-age on metered front-door responses; default `cacheTtlSeconds`. */
  meteredBrowserMaxAgeSeconds?: number;
}

/** Who receives the tile bytes — decides public vs private Cache-Control. */
export type CacheResponseRole = 'edge-store' | 'cdn-browser' | 'metered-browser';

const DAY = 24 * 60 * 60;

export const MAP_PROVIDERS: readonly MapProvider[] = [
  {
    id: 'osm',
    label: 'OpenStreetMap',
    namespace: 'map',
    kind: 'base',
    upstream: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    extension: 'png',
    contentType: 'image/png',
    maxZoom: 20,
    maxNativeZoom: 19,
    attribution: '© OpenStreetMap contributors',
    // OSMF Tile Usage Policy expects heavy users to cache aggressively; 30 d R2/edge
    // plus immutable browser caching on the public CDN keeps origin load down.
    cacheTtlSeconds: 30 * DAY,
    termsStatus: 'confirmed',
    // The OSM Foundation's Tile Usage Policy asks heavy consumers to cache
    // rather than hit the origin, and to send an identifying User-Agent —
    // both of which this route does. Data is ODbL; attribution is required
    // and is served per tile plus in /meta/map/providers.
    termsNote: 'OSMF Tile Usage Policy — caching by heavy users expected; identifying User-Agent sent; ODbL attribution served.',
    publicCdn: true,
    cachePolicy: {
      browserDirectives: ['immutable'],
      cdnBrowserMaxAgeSeconds: 30 * DAY,
      meteredBrowserMaxAgeSeconds: 30 * DAY,
    },
  },
  {
    id: 'openseamap-seamark',
    label: 'OpenSeaMap seamarks',
    namespace: 'map',
    kind: 'overlay',
    upstream: 'https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png',
    extension: 'png',
    contentType: 'image/png',
    maxZoom: 20,
    maxNativeZoom: 18,
    attribution: '© OpenSeaMap contributors',
    cacheTtlSeconds: 7 * DAY,
    termsStatus: 'prototype',
    termsNote: 'Tony, 30 Oct 2026: behind the front door so every tile is counted. OpenSeaMap publishes no tile usage policy as explicit as OSMF\'s; confirm proxy + 7-day cache with the project before the Scout change ships.',
    publicCdn: false,
  },
  {
    id: 'opentopomap',
    label: 'OpenTopoMap',
    namespace: 'map',
    kind: 'base',
    upstream: 'https://tile.opentopomap.org/{z}/{x}/{y}.png',
    extension: 'png',
    contentType: 'image/png',
    maxZoom: 19,
    maxNativeZoom: 17,
    attribution: '© OpenStreetMap contributors, SRTM | © OpenTopoMap (CC-BY-SA)',
    cacheTtlSeconds: 30 * DAY,
    termsStatus: 'prototype',
    termsNote: 'Tony, 30 Oct 2026: behind the front door so every tile is counted. OpenTopoMap asks heavy users not to bulk-fetch its tiles; confirm proxy + cache with the project before the Scout change ships.',
    publicCdn: false,
  },
  {
    // ZYX, like every ArcGIS MapServer tile route. Anonymous `arcgisonline`
    // is the free service and bills nobody; `ibasemaps-api` is the metered one
    // the Licensing Agent already uses (vortex-api `sourceRegistry.ts`). The
    // Worker picks the keyed host whenever the secret is set, so web traffic
    // lands on the same account the Esri dashboard reconciles against.
    id: 'esri-imagery',
    label: 'Esri World Imagery',
    namespace: 'imagery',
    kind: 'base',
    upstream: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    keyedUpstream: 'https://ibasemaps-api.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}?token={key}',
    credentialRef: 'esri',
    extension: 'jpg',
    contentType: 'image/jpeg',
    maxZoom: 20,
    maxNativeZoom: 19,
    attribution: 'Esri, Maxar, Earthstar Geographics',
    cacheTtlSeconds: 30 * DAY,
    // CONFIRMED by Tony, 29 Oct 2026: Esri tiles may be proxied and cached (it was on
    // `prototype` from Myles's decision of 2026-09-13 while the agreement was read). The
    // anonymous arcgisonline service is unmetered; the keyed host is the account Esri bills.
    termsStatus: 'confirmed',
    termsNote: 'Confirmed by Tony, 29 Oct 2026: proxying and caching Esri tiles is permitted.',
    publicCdn: true,
    cachePolicy: { cdnBrowserMaxAgeSeconds: 30 * DAY, meteredBrowserMaxAgeSeconds: 30 * DAY },
    // The Scout's Licensing Agent asks for World Imagery down to 23 (sourceRegistry.ts).
    frontDoorMaxZoom: 23,
  },
  {
    // ZYX WMTS. The vintage is pinned in the path: the three web apps each
    // hardcoded a different year (2021 / 2024 / 2025). 2025 is the newest and
    // matches vortex-api `sourceRegistry.ts`. Bumping it is a registry edit
    // plus a `v` bump on the route, so caches never mix two mosaics.
    id: 'sentinel2-cloudless',
    label: 'Sentinel-2 cloudless',
    namespace: 'imagery',
    kind: 'base',
    upstream: 'https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2025_3857/default/GoogleMapsCompatible/{z}/{y}/{x}.jpg',
    extension: 'jpg',
    contentType: 'image/jpeg',
    maxZoom: 19,
    maxNativeZoom: 15,
    attribution: 'Sentinel-2 cloudless 2025 by EOX (CC BY-NC-SA 4.0)',
    cacheTtlSeconds: 30 * DAY,
    // Tony, 29 Oct 2026: served and cached through the tile front door and never charged
    // to customers. EOX publish s2cloudless under CC BY-NC-SA 4.0 (NonCommercial); serving
    // it free of charge is Tony's decision on the record. Not on the public CDN.
    termsStatus: 'confirmed',
    termsNote: 'Tony, 29 Oct 2026: served and cached through the front door, never charged. Licence CC BY-NC-SA 4.0 (NonCommercial).',
    publicCdn: false,
    charged: false,
    cachePolicy: { meteredBrowserMaxAgeSeconds: 30 * DAY },
  },
  {
    // ZYX, like World Imagery. Rendered bathymetry and coastlines, so it is cartography.
    id: 'esri-ocean',
    label: 'Esri World Ocean Base',
    namespace: 'map',
    kind: 'base',
    upstream: 'https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}',
    keyedUpstream: 'https://ibasemaps-api.arcgis.com/arcgis/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}?token={key}',
    credentialRef: 'esri',
    extension: 'jpg',
    contentType: 'image/jpeg',
    maxZoom: 16,
    maxNativeZoom: 16,
    attribution: 'Esri, Garmin, GEBCO, NOAA NGDC, and other contributors',
    cacheTtlSeconds: 30 * DAY,
    termsStatus: 'confirmed',
    termsNote: 'Confirmed by Tony, 29 Oct 2026: proxying and caching Esri tiles is permitted.',
    publicCdn: false,
    cachePolicy: { meteredBrowserMaxAgeSeconds: 30 * DAY },
  },
  {
    // The free endpoint the Scout draws today. Google's licensed route is the Map Tiles API,
    // a different address with session tokens: it replaces this when Tony supplies the key.
    id: 'google-satellite',
    label: 'Google satellite',
    namespace: 'imagery',
    kind: 'base',
    upstream: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
    extension: 'jpg',
    contentType: 'image/jpeg',
    maxZoom: 21,
    maxNativeZoom: 20,
    attribution: '© Google',
    cacheTtlSeconds: 30 * DAY,
    termsStatus: 'prototype',
    termsNote: 'Tony, 30 Oct 2026: temporary, unlicensed. Free endpoint behind the front door until Tony supplies a licensed Google key.',
    publicCdn: false,
  },
  {
    // Satellite with roads and place names, which change more often than the imagery.
    id: 'google-hybrid',
    label: 'Google hybrid',
    namespace: 'imagery',
    kind: 'base',
    upstream: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
    extension: 'jpg',
    contentType: 'image/jpeg',
    maxZoom: 21,
    maxNativeZoom: 20,
    attribution: '© Google',
    cacheTtlSeconds: 7 * DAY,
    termsStatus: 'prototype',
    termsNote: 'Tony, 30 Oct 2026: temporary, unlicensed. Free endpoint behind the front door until Tony supplies a licensed Google key.',
    publicCdn: false,
  },
  {
    // Bing's retired anonymous aerial endpoint, as the Scout's bingProtocol uses it. Where
    // Bing has no imagery it answers 200 with a PNG placeholder, which the front door
    // treats as no tile because it is not a JPEG.
    id: 'bing-aerial',
    label: 'Bing aerial',
    namespace: 'imagery',
    kind: 'base',
    upstream: 'https://ecn.t0.tiles.virtualearth.net/tiles/a{quadkey}.jpeg?g=14237',
    extension: 'jpg',
    contentType: 'image/jpeg',
    maxZoom: 20,
    maxNativeZoom: 19,
    attribution: '© Microsoft',
    cacheTtlSeconds: 30 * DAY,
    termsStatus: 'prototype',
    termsNote: 'Tony, 30 Oct 2026: temporary, unlicensed. Free endpoint behind the front door until Tony supplies a licensed Microsoft key.',
    publicCdn: false,
  },
  {
    // Azure Maps imagery has no free address: it needs our subscription key
    // (AZURE_MAPS_KEY on vortex-tiles). Until that is set the Scout draws it direct.
    id: 'azure-imagery',
    label: 'Azure Maps imagery',
    namespace: 'imagery',
    kind: 'base',
    upstream: 'https://atlas.microsoft.com/map/tile?api-version=2024-04-01&tilesetId=microsoft.imagery&zoom={z}&x={x}&y={y}',
    keyedUpstream: 'https://atlas.microsoft.com/map/tile?api-version=2024-04-01&tilesetId=microsoft.imagery&zoom={z}&x={x}&y={y}&subscription-key={key}',
    credentialRef: 'azure',
    credentialRequired: true,
    extension: 'jpg',
    contentType: 'image/jpeg',
    maxZoom: 20,
    maxNativeZoom: 19,
    attribution: '© Microsoft, © TomTom',
    cacheTtlSeconds: 30 * DAY,
    termsStatus: 'prototype',
    termsNote: 'Tony, 30 Oct 2026: behind the front door with our key; Azure Maps terms on caching not yet read.',
    publicCdn: false,
  },
  {
    // Official ENC charts rendered by SiiTech Chart Server. Documented options, from
    // https://www.siitech.com/Products/Chart-Server/Chart-Server-API (read 29 Oct 2026):
    //   c  colour mode   1 Bright, 2 White back, 3 Black back, 4 Dusk (black back), 5 Night,
    //                    6 Night bright. Blank = Bright.
    //   m  display mode  1 Base, 2 Standard, 3 Other. Blank = Standard.
    //   zoom 0 to 21.
    // Not in that document, used as the Scout and PassageWiki use them:
    //   w  1 the chart over water only (an overlay), 0 land as well (SiiTech's examples
    //      offer "water only" pages; the parameter itself is not documented)
    //   mu 1 soundings in feet; 0, the default, metres
    // Charts are corrected weekly, so a copy is kept a week.
    id: 'siitech-enc',
    label: 'Siitech ENC charts',
    namespace: 'map',
    kind: 'overlay',
    upstream: 'https://encs2.siitech.com/tileWorld/{z}/{x}/{y}.png?c={c}&m={m}&w={w}',
    keyedUpstream: 'https://encs2.siitech.com/tileWorld/{z}/{x}/{y}.png?c={c}&m={m}&w={w}&key={key}',
    credentialRef: 'siitech',
    extension: 'png',
    contentType: 'image/png',
    maxZoom: 22,
    // SiiTech documents zoom 0 to 21; past that a client upscales.
    maxNativeZoom: 21,
    attribution: 'ENC charts © Siitech and the issuing hydrographic offices',
    cacheTtlSeconds: 7 * DAY,
    termsStatus: 'confirmed',
    termsNote: 'Tony, 29 Oct 2026: Siitech authorises shared caching, and download to Scouts for offline use.',
    publicCdn: false,
    // ENC updates weekly — do not retain or advertise longer than a correction cycle.
    cachePolicy: { meteredBrowserMaxAgeSeconds: 7 * DAY },
    variants: {
      c: { values: ['1', '2', '3', '4', '5', '6'], default: '1' },
      // Full detail by default (Tony, 29 Oct 2026): Other adds contours, light descriptions
      // and more over Standard.
      m: { values: ['1', '2', '3'], default: '3' },
      w: { values: ['0', '1'], default: '1' },
      mu: { values: ['0', '1'], default: '0' },
    },
  },
];

/** Route version. Bump to orphan every cached tile of every provider. */
export const MAP_ROUTE_VERSION = 'v1';

export function mapProviderById(id: string): MapProvider | undefined {
  return MAP_PROVIDERS.find((p) => p.id === id);
}

/**
 * The Licensing Agent's names for the same layers (vortex-api `sourceRegistry.ts`), so a
 * Scout can ask the tile front door by the id it already uses.
 */
export const SOURCE_ALIASES: Readonly<Record<string, string>> = {
  'esri-satellite': 'esri-imagery',
  'sentinel-2': 'sentinel2-cloudless',
  'google-satellite-free': 'google-satellite',
  'google-hybrid-free': 'google-hybrid',
  'bing-satellite': 'bing-aerial',
  'azure-satellite': 'azure-imagery',
};

/** A provider by its registry id or a Scout alias. */
export function providerForSource(id: string): MapProvider | undefined {
  return mapProviderById(SOURCE_ALIASES[id] ?? id);
}

export function isMapProviderId(value: unknown): value is string {
  return typeof value === 'string' && MAP_PROVIDERS.some((p) => p.id === value);
}

/** On the public CDN: licensed for it, and chosen to be open without a pass. */
export const onPublicCdn = (p: MapProvider): boolean => isServable(p.termsStatus) && p.publicCdn;

/** The deepest zoom the tile front door fetches for a provider. */
export const frontDoorMaxZoom = (p: MapProvider): number => p.frontDoorMaxZoom ?? p.maxNativeZoom;

/** A provider's options from a request: each one asked for, or its default. Null if one is outside its set. */
export function resolveVariant(p: MapProvider, params: URLSearchParams): Record<string, string> | null {
  const out: Record<string, string> = {};
  for (const [name, v] of Object.entries(p.variants ?? {})) {
    const asked = params.get(name) ?? v.default;
    if (!v.values.includes(asked)) return null;
    out[name] = asked;
  }
  return out;
}

/** The options as one cache-key segment, in the registry's order; '' for a provider with none. */
export function variantKey(p: MapProvider, variant: Record<string, string>): string {
  return Object.keys(p.variants ?? {}).map((name) => `${name}${variant[name]}`).join('-');
}

/** Providers the CDN will actually serve today. */
export const proxiedProviders = (): MapProvider[] => MAP_PROVIDERS.filter((p) => isServable(p.termsStatus));

/** Providers waiting on a licence read, with the reason. */
export const pendingProviders = (): MapProvider[] => MAP_PROVIDERS.filter((p) => !isServable(p.termsStatus));

/** Served on an explicit decision rather than a licence review — must not ship. */
export const prototypeProviders = (): MapProvider[] =>
  MAP_PROVIDERS.filter((p) => p.termsStatus === 'prototype');

/** Tile coordinates that exist at this zoom. Rejects the deep-zoom scrapes a 404 should stop. */
export function isValidTile(z: number, x: number, y: number): boolean {
  if (![z, x, y].every((n) => Number.isInteger(n) && n >= 0)) return false;
  if (z > 22) return false;
  const n = 2 ** z;
  return x < n && y < n;
}

/** Bing's quadkey: one base-4 digit per zoom level, from the x and y bits at that level. */
export function quadkey(z: number, x: number, y: number): string {
  let key = '';
  for (let i = z; i > 0; i--) {
    const mask = 1 << (i - 1);
    key += String((x & mask ? 1 : 0) + (y & mask ? 2 : 0));
  }
  return key;
}

/** The upstream URL for one tile, with the credential substituted when held. */
export function upstreamTileUrl(
  provider: MapProvider,
  z: number,
  x: number,
  y: number,
  key?: string,
  variant: Record<string, string> = {},
): string {
  const template = key && provider.keyedUpstream ? provider.keyedUpstream : provider.upstream;
  let url = template
    .replace('{z}', String(z))
    .replace('{x}', String(x))
    .replace('{y}', String(y))
    .replace('{quadkey}', quadkey(z, x, y))
    .replace('{key}', encodeURIComponent(key ?? ''));
  for (const [name, v] of Object.entries(provider.variants ?? {})) {
    const value = variant[name] ?? v.default;
    if (url.includes(`{${name}}`)) url = url.replace(`{${name}}`, encodeURIComponent(value));
    else if (value !== v.default) url += `&${name}=${encodeURIComponent(value)}`;
  }
  return url;
}

function directiveSuffix(directives: readonly string[] | undefined): string {
  if (!directives?.length) return '';
  return `, ${directives.join(', ')}`;
}

/**
 * Cache-Control for tile responses. Miss tiles use a short TTL (`missMaxAgeSeconds`, default 300).
 */
export function cacheControlHeader(provider: MapProvider, role: CacheResponseRole): string {
  if (role === 'edge-store') {
    return `public, max-age=${provider.cacheTtlSeconds}${directiveSuffix(provider.cachePolicy?.browserDirectives)}`;
  }
  const policy = provider.cachePolicy;
  if (role === 'cdn-browser') {
    const maxAge = policy?.cdnBrowserMaxAgeSeconds ?? provider.cacheTtlSeconds;
    return `public, max-age=${maxAge}${directiveSuffix(policy?.browserDirectives)}`;
  }
  const maxAge = policy?.meteredBrowserMaxAgeSeconds ?? provider.cacheTtlSeconds;
  return `private, max-age=${maxAge}${directiveSuffix(policy?.browserDirectives)}`;
}

/** Cache-Control when upstream returned nothing — same visibility as the surrounding route. */
export function cacheControlMiss(_provider: MapProvider, role: 'cdn-browser' | 'metered-browser', missMaxAgeSeconds = 300): string {
  const visibility = role === 'cdn-browser' ? 'public' : 'private';
  return `${visibility}, max-age=${missMaxAgeSeconds}`;
}
